import type { CompensationLine } from '@/types/compensation';

import { toReaderLocale } from '@/utils/numberFormat';

/** The six sortable columns of the adjustments table. */
export type CompensationLineSortKey =
    | 'name'
    | 'score'
    | 'salary'
    | 'proposedPercent'
    | 'appliedPercent'
    | 'newSalary';

export type SortDirection = 'asc' | 'desc';

export interface SortLinesOptions {
    lines: CompensationLine[];
    sortKey: CompensationLineSortKey;
    direction: SortDirection;
    /** Only used by the `name` key, for a locale-correct `localeCompare`. */
    language: string;
}

interface NumericValueOptions {
    line: CompensationLine;
    sortKey: CompensationLineSortKey;
}

/** The figure a numeric column sorts on, or `null` when this person has none. Object-taking like
 * `sortLines` itself, so the module reads one way throughout. */
const numericValue = ({ line, sortKey }: NumericValueOptions): number | null => {
    switch (sortKey) {
        case 'score':
            return line.score;
        case 'salary':
            return line.salary;
        case 'proposedPercent':
            return line.proposedPercent;
        case 'appliedPercent':
            return line.appliedPercent;
        case 'newSalary':
            return line.newSalary;
        case 'name':
            return null;
    }
};

/**
 * Sorts the adjustments table's lines by one of its six sortable columns. `null` values (no score,
 * no known salary, no new salary) always sort last regardless of direction, so switching direction
 * never buries a fully-known row under a pile of dashes.
 */
export const sortLines = ({
    lines,
    sortKey,
    direction,
    language,
}: SortLinesOptions): CompensationLine[] => {
    const sign = direction === 'asc' ? 1 : -1;

    return [...lines].sort((a, b) => {
        if (sortKey === 'name') {
            return sign * a.displayName.localeCompare(b.displayName, toReaderLocale(language));
        }

        const valueA = numericValue({ line: a, sortKey });
        const valueB = numericValue({ line: b, sortKey });

        if (valueA === null && valueB === null) {
            return 0;
        }
        if (valueA === null) {
            return 1;
        }
        if (valueB === null) {
            return -1;
        }

        return sign * (valueA - valueB);
    });
};
